import { markdownToHtml } from "./msgUtils";

function cleanEmailText(text) {
  return text
    .replace(/<[^>]*>/g, "") // HTML tags
    .replace(/^\s*$/gm, "") // Empty lines
    .replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, ""); // Zero-width spaces
}

export function formatEmailText(sender, recipient, subject, text, dropFirstEmail = false) {
  text = cleanEmailText(text);
  let emails = text.split(/(?=\n\s*From:)/g);

  if (dropFirstEmail && emails.length > 1) {
    emails = emails.slice(1);
  }

  return emails
    .map((email, index) => {
      const fromMatch = email.match(/From: (.+)/);
      const toMatch = email.match(/To: (.+)/);
      const dateMatch = email.match(/Sent: (.+)/);

      const body = email.replace(/From: .+\n|To: .+\n|Sent: .+\n/g, "").trim();
      let formattedEmail = "";

      if (index === 1) {
        formattedEmail += "\n\nPrevious emails in the thread:\n\n";
      }

      formattedEmail += `From: ${fromMatch ? fromMatch[1] : sender}\n`;
      formattedEmail += `To: ${toMatch ? toMatch[1] : recipient}\n`;
      formattedEmail += `Date: ${dateMatch ? dateMatch[1] : "Unknown"}\n`;
      formattedEmail += `Subject: ${subject}\n\n`;
      formattedEmail += `${body}\n`;

      return formattedEmail;
    })
    .join("\n");
}

export function containsHtmlTags(str) {
  const pattern = /<[^>]+>/;
  return pattern.test(str);
}

const ALLOWED_TAGS = new Set([
  "P",
  "DIV",
  "BR",
  "UL",
  "OL",
  "LI",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "A",
  "SPAN",
  "TABLE",
  "THEAD",
  "TBODY",
  "TR",
  "TH",
  "TD",
]);
const BLOCK_TAGS = new Set(["P", "DIV", "LI"]);
const TABLE_TAGS = new Set(["TABLE", "THEAD", "TBODY", "TR", "TH", "TD"]);
const TYPOGRAPHY_STYLE_PROPS = [
  "font-family",
  "font-size",
  "color",
  "line-height",
  "letter-spacing",
  "font-weight",
  "font-style",
  "font-variant",
  "text-decoration-color",
];

const SIGNATURE_LINE_RE =
  /^(Warm regards|Sincerely|Regards|Cheers|Best regards|Thank you|Thanks|Best|Kind regards),?$/i;
const SKIP_BLOCK_RE = /^(Certainly|Subject:)/i;

const parseMarkdownTableCells = (text) => {
  const trimmed = text.trim();
  if (!trimmed.startsWith("|")) {
    return [];
  }
  return trimmed
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
};

const isMarkdownTableRowText = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed.startsWith("|")) {
    return false;
  }
  const cells = parseMarkdownTableCells(trimmed);
  return cells.length >= 2;
};

const isMarkdownTableSeparatorRow = (cells) =>
  cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$|^[\-\s:|]+$/.test(cell));

const expandMarkdownTableRowTexts = (rowTexts) =>
  rowTexts.flatMap((text) => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length > 1 && lines.every(isMarkdownTableRowText)) {
      return lines;
    }
    return [text];
  });

const isMarkdownTableRowParagraph = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const tag = node.tagName;
  if (tag !== "P" && tag !== "DIV") {
    return false;
  }
  return isMarkdownTableRowText(node.textContent);
};

const MARKDOWN_BULLET_LINE_RE = /^\s*[-*•]\s+([\s\S]+)$/;

const getMarkdownBulletItemText = (text) => {
  const match = (text || "").trim().match(MARKDOWN_BULLET_LINE_RE);
  return match ? match[1].trim() : null;
};

const splitBrSeparatedBulletTexts = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const tag = node.tagName;
  if (tag !== "P" && tag !== "DIV") {
    return null;
  }
  const lines = node.innerHTML
    .split(/<br\s*\/?>/i)
    .map((part) => {
      const el = node.ownerDocument.createElement("div");
      el.innerHTML = part;
      return el.textContent.trim();
    })
    .filter(Boolean);
  if (lines.length < 2 || !lines.every((line) => getMarkdownBulletItemText(line))) {
    return null;
  }
  return lines.map((line) => getMarkdownBulletItemText(line));
};

const isMarkdownBulletParagraph = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const tag = node.tagName;
  if (tag !== "P" && tag !== "DIV") {
    return false;
  }
  if (getMarkdownBulletItemText(node.textContent)) {
    return true;
  }
  return splitBrSeparatedBulletTexts(node) !== null;
};

const collectMarkdownBulletParagraphs = (childNodes, startIndex) => {
  const itemTexts = [];
  let index = startIndex;

  while (index < childNodes.length) {
    const current = childNodes[index];
    if (isIgnorableNodeBetweenBlocks(current)) {
      if (index + 1 < childNodes.length && isMarkdownBulletParagraph(childNodes[index + 1])) {
        index += 1;
        continue;
      }
      break;
    }
    if (!isMarkdownBulletParagraph(current)) {
      break;
    }

    const brItems = splitBrSeparatedBulletTexts(current);
    if (brItems) {
      itemTexts.push(...brItems);
    } else {
      const text = getMarkdownBulletItemText(current.textContent);
      if (text) {
        itemTexts.push(text);
      }
    }
    index += 1;
  }

  return { itemTexts, nextIndex: index };
};

const buildOutlookBulletList = (doc, itemTexts) => {
  if (itemTexts.length === 0) {
    return null;
  }
  const ul = doc.createElement("ul");
  itemTexts.forEach((text) => {
    const li = doc.createElement("li");
    li.textContent = text;
    ul.appendChild(li);
  });
  return ul;
};

const isIgnorableNodeBetweenBlocks = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent.replace(/\u00a0/g, " ").trim() === "";
  }
  return false;
};

const applyOutlookTableStyles = (table) => {
  table.style.borderCollapse = "collapse";
  table.style.width = "100%";
  table.style.margin = "8px 0";
};

const applyOutlookCellStyles = (cell, isHeader) => {
  cell.style.border = "1px solid #CCCCCC";
  cell.style.padding = "4px 8px";
  if (isHeader) {
    cell.style.textAlign = "left";
    cell.style.fontWeight = "600";
  }
};

const buildOutlookTable = (doc, rowTexts) => {
  const expandedRowTexts = expandMarkdownTableRowTexts(rowTexts);
  const parsedRows = expandedRowTexts.map(parseMarkdownTableCells).filter((row) => row.length > 0);
  if (parsedRows.length === 0) {
    return null;
  }

  let headerCells = null;
  let bodyRows = parsedRows;

  if (parsedRows.length >= 2 && isMarkdownTableSeparatorRow(parsedRows[1])) {
    headerCells = parsedRows[0];
    bodyRows = parsedRows.slice(2);
  }

  const table = doc.createElement("table");

  if (headerCells) {
    const thead = doc.createElement("thead");
    const headerRow = doc.createElement("tr");
    headerCells.forEach((cellText) => {
      const th = doc.createElement("th");
      th.textContent = cellText;
      applyOutlookCellStyles(th, true);
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);
  }

  const tbody = doc.createElement("tbody");
  bodyRows.forEach((cells) => {
    if (isMarkdownTableSeparatorRow(cells)) {
      return;
    }
    const row = doc.createElement("tr");
    cells.forEach((cellText) => {
      const td = doc.createElement("td");
      td.textContent = cellText;
      applyOutlookCellStyles(td, false);
      row.appendChild(td);
    });
    tbody.appendChild(row);
  });
  table.appendChild(tbody);
  applyOutlookTableStyles(table);

  return table;
};

const escapeHtml = (text) => {
  const el = document.createElement("div");
  el.textContent = text;
  return el.innerHTML;
};

const wrapPlainTextAsOutlookHtml = (text) => {
  const lines = text.split("\n");
  const parts = [];
  let index = 0;

  while (index < lines.length) {
    if (isMarkdownTableRowText(lines[index])) {
      const rowLines = [];
      while (index < lines.length && isMarkdownTableRowText(lines[index])) {
        rowLines.push(lines[index]);
        index += 1;
      }
      const doc = new DOMParser().parseFromString("", "text/html");
      const table = buildOutlookTable(doc, rowLines);
      if (table) {
        parts.push(table.outerHTML);
      }
      continue;
    }

    const line = lines[index];
    if (line.trim() === "") {
      parts.push("&nbsp;");
    } else {
      parts.push(`<p class="MsoNormal" style="margin:0">${escapeHtml(line)}</p>`);
    }
    index += 1;
  }

  return parts.join("");
};

const shouldSkipBlock = (element) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const text = element.textContent.trim();
  return SIGNATURE_LINE_RE.test(text) || SKIP_BLOCK_RE.test(text);
};

const stripTypographyStyles = (element) => {
  if (!element.style) {
    return;
  }
  TYPOGRAPHY_STYLE_PROPS.forEach((prop) => {
    element.style.removeProperty(prop);
  });
  if (!element.getAttribute("style")?.trim()) {
    element.removeAttribute("style");
  }
};

const cleanElementAttributes = (element, tagName) => {
  const href = tagName === "A" ? element.getAttribute("href") : null;
  while (element.attributes.length > 0) {
    element.removeAttribute(element.attributes[0].name);
  }
  if (href) {
    element.setAttribute("href", href);
  }
};

const normalizeBlockElement = (element) => {
  element.className = "MsoNormal";
  stripTypographyStyles(element);
  element.style.margin = "0";
};

const unwrapElement = (element) => {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

const normalizeElementTree = (element) => {
  const tagName = element.tagName;

  if (TABLE_TAGS.has(tagName)) {
    if (element.style) {
      element.style.removeProperty("font-family");
      element.style.removeProperty("font-size");
      element.style.removeProperty("color");
      element.style.removeProperty("line-height");
    }
    [...element.childNodes].forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        normalizeElementTree(child);
      }
    });
    return;
  }

  if (tagName === "FONT") {
    const children = [...element.childNodes];
    unwrapElement(element);
    children.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        normalizeElementTree(child);
      }
    });
    return;
  }

  if (!ALLOWED_TAGS.has(tagName)) {
    const children = [...element.childNodes];
    unwrapElement(element);
    children.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        normalizeElementTree(child);
      }
    });
    return;
  }

  cleanElementAttributes(element, tagName);
  stripTypographyStyles(element);

  if (BLOCK_TAGS.has(tagName)) {
    normalizeBlockElement(element);
  }

  [...element.childNodes].forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      normalizeElementTree(child);
    }
  });

  if (tagName === "SPAN" && element.attributes.length === 0 && element.parentNode) {
    unwrapElement(element);
  }
};

const collectMarkdownTableRows = (childNodes, startIndex) => {
  const rowTexts = [];
  let index = startIndex;

  while (index < childNodes.length) {
    const current = childNodes[index];
    if (isIgnorableNodeBetweenBlocks(current)) {
      if (index + 1 < childNodes.length && isMarkdownTableRowParagraph(childNodes[index + 1])) {
        index += 1;
        continue;
      }
      break;
    }
    if (!isMarkdownTableRowParagraph(current)) {
      break;
    }
    rowTexts.push(current.textContent);
    index += 1;
  }

  return { rowTexts, nextIndex: index };
};

const appendNormalizedNode = (doc, parts, node) => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    if (shouldSkipBlock(node)) {
      return;
    }
    normalizeElementTree(node);
    parts.push(node.outerHTML);
  } else if (node.nodeType === Node.TEXT_NODE) {
    if (parts.length > 0 && node.textContent.replace(/\u00a0/g, " ").trim() === "") {
      parts.push("&nbsp;");
    } else if (node.textContent.trim()) {
      const paragraph = doc.createElement("p");
      paragraph.className = "MsoNormal";
      paragraph.style.margin = "0";
      paragraph.textContent = node.textContent;
      parts.push(paragraph.outerHTML);
    }
  }
};

/**
 * Normalize bot HTML for Outlook insertion: preserve structure (bold, lists, links, tables),
 * strip backend typography so Outlook applies the compose window defaults.
 */
export function normalizeOutlookDraftHtml(html) {
  if (!html || typeof html !== "string") {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const parts = [];
  const childNodes = [...doc.body.childNodes];
  let index = 0;

  while (index < childNodes.length) {
    const node = childNodes[index];

    if (isMarkdownTableRowParagraph(node)) {
      const { rowTexts, nextIndex } = collectMarkdownTableRows(childNodes, index);
      const table = buildOutlookTable(doc, rowTexts);
      if (table) {
        normalizeElementTree(table);
        parts.push(table.outerHTML);
      }
      index = nextIndex;
      continue;
    }

    if (isMarkdownBulletParagraph(node)) {
      const { itemTexts, nextIndex } = collectMarkdownBulletParagraphs(childNodes, index);
      const list = buildOutlookBulletList(doc, itemTexts);
      if (list) {
        normalizeElementTree(list);
        parts.push(list.outerHTML);
      }
      index = nextIndex;
      continue;
    }

    appendNormalizedNode(doc, parts, node);
    index += 1;
  }

  if (parts.length === 0) {
    return html;
  }

  return parts.join("");
}

const isPlainTextBodyType = (bodyType) => {
  if (bodyType == null) {
    return false;
  }
  if (bodyType === "text") {
    return true;
  }
  if (typeof Office !== "undefined") {
    if (Office.CoercionType && bodyType === Office.CoercionType.Text) {
      return true;
    }
    if (Office.MailboxEnums?.BodyType && bodyType === Office.MailboxEnums.BodyType.Text) {
      return true;
    }
  }
  return false;
};

export function prepareRichDraftHtml(text, bodyType) {
  let output = text;
  if (!containsHtmlTags(output)) {
    output = markdownToHtml(output);
  }
  if (!containsHtmlTags(output)) {
    output = wrapPlainTextAsOutlookHtml(output);
  }
  output = normalizeOutlookDraftHtml(output);

  if (isPlainTextBodyType(bodyType)) {
    return output.replace(/<\/p>/g, "</p><br>");
  }
  return output;
}

export function cleanDraftText(text, officeType, bodyType) {
  let output = text;

  if (officeType === "OUTLOOK") {
    return prepareRichDraftHtml(output, bodyType);
  }

  output = output.replace(/<p>/g, '<p class="MsoNormal" style="margin-top:0px;margin-bottom:0px">');

  if (!containsHtmlTags(output)) {
    return output
      .split("\n")
      .map((line) =>
        line.trim() === "" ? "&nbsp;" : `<p class="MsoNormal" style="margin-top:0px;margin-bottom:0px">${line}</p>`
      )
      .join("");
  }

  return output;
}
